/**
 * GraphExplorerPro - Linkurious-style Graph Explorer
 *
 * Professional graph exploration UI with:
 * - Left panel: Selected node properties
 * - Center: Interactive force-directed graph
 * - Right panel: Filters (node types, edges, property ranges)
 */

import React, { useRef, useCallback, useEffect, useState, useMemo } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Stack,
  CircularProgress,
  TextField,
  InputAdornment,
  Tooltip,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  X, ChevronLeft, ChevronRight, Filter, Eye, EyeOff,
  ZoomIn, ZoomOut, Maximize, Target, Crosshair, Search,
  Circle, ArrowRight, Network, SearchX
} from 'lucide-react';

import UserDetailPopup from './UserDetailPopup';

// Node colors by type - vibrant neon palette for dark canvas
const NODE_COLORS = {
  User: '#34d399',                    // Emerald green
  Group: '#60a5fa',                   // Sky blue
  Application: '#dba87a',             // Warm orange
  Repository: '#818cf8',              // Indigo (GitHub repos)
  JiraProject: '#2dd4bf',             // Teal (Jira projects)
  Department: '#a78bfa',              // Violet
  Role: '#f87171',                    // Coral red
  Tenant: '#e879f9',                  // Fuchsia
  ExcessiveAccessInsight: '#f472b6',  // Hot pink
  PrivilegeCreepInsight: '#fb7185',   // Rose
  DormantUserInsight: '#c084fc',      // Purple
  default: '#94a3b8',                 // Slate
};

// Node glow colors (slightly brighter for outer halo)
const NODE_GLOW_COLORS = {
  User: 'rgba(52, 211, 153, 0.35)',
  Group: 'rgba(96, 165, 250, 0.35)',
  Application: 'rgba(251, 146, 60, 0.35)',
  Repository: 'rgba(129, 140, 248, 0.35)',
  JiraProject: 'rgba(45, 212, 191, 0.35)',
  Department: 'rgba(167, 139, 250, 0.35)',
  Role: 'rgba(248, 113, 113, 0.35)',
  Tenant: 'rgba(232, 121, 249, 0.35)',
  ExcessiveAccessInsight: 'rgba(244, 114, 182, 0.4)',
  PrivilegeCreepInsight: 'rgba(251, 113, 133, 0.4)',
  DormantUserInsight: 'rgba(192, 132, 252, 0.4)',
  default: 'rgba(148, 163, 184, 0.25)',
};

// Edge colors by relationship type
const EDGE_COLORS = {
  MEMBER_OF: '#60a5fa',
  HAS_MEMBER: '#60a5fa',
  BELONGS_TO: '#60a5fa',
  HAS_ACCESS_TO: '#34d399',
  ASSIGNED_TO: '#34d399',
  HOSTS: '#e879f9',
  HAS_ROLE: '#a78bfa',
  REPORTS_TO: '#94a3b8',
  MANAGES: '#94a3b8',
  HAS_INSIGHT: '#f472b6',
  AFFECTS: '#fb7185',
  RELATED: '#475569',
  default: '#475569',
};

// Node icons/shapes
const NODE_SHAPES = {
  User: 'circle',
  Group: 'hexagon',
  Application: 'square',
  Repository: 'pentagon',
  JiraProject: 'triangle',
  Department: 'diamond',
  DormantUserInsight: 'star',
  ExcessiveAccessInsight: 'star',
  PrivilegeCreepInsight: 'star',
  default: 'circle',
};

// Node icons (emoji rendered inside shape)
const NODE_ICONS = {
  User: '\u{1F464}',
  Group: '\u{1F465}',
  Application: '⬡',
  Repository: '\u{1F4E6}',
  JiraProject: '\u{1F4CB}',
  Department: '\u{1F3E2}',
  Role: '\u{1F511}',
  ExcessiveAccessInsight: '⚠',
  PrivilegeCreepInsight: '\u{1F4C8}',
  DormantUserInsight: '\u{1F4A4}',
  default: '●',
};

// Node sizes by type (larger for dark canvas visibility)
const NODE_SIZES = {
  User: 14,
  Group: 18,
  Application: 16,
  Repository: 16,
  JiraProject: 16,
  Department: 20,
  Tenant: 20,
  Role: 14,
  ExcessiveAccessInsight: 22,
  PrivilegeCreepInsight: 22,
  DormantUserInsight: 22,
  default: 12,
};

// Format property keys for display
const formatPropertyKey = (key) => {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/^\w/, c => c.toUpperCase())
    .trim();
};

// Format property values
const formatPropertyValue = (value) => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

// Get risk color
const getRiskColor = (score) => {
  if (score === undefined || score === null) return undefined;
  const num = Number(score);
  if (num >= 80) return '#d27373';
  if (num >= 60) return '#d4925e';
  if (num >= 40) return '#eab308';
  if (num >= 20) return '#5da87a';
  return '#6b7280';
};

// Decision ring colors for review mode
const DECISION_COLORS = {
  approve: '#5da87a',
  flag: '#eab308',
  revoke: '#d27373',
};

// Panel styles shared between left panels
const panelSx = {
  position: 'absolute',
  top: 0,
  bottom: 0,
  width: 260,
  bgcolor: 'background.paper',
  borderRight: 1,
  borderColor: 'divider',
  zIndex: 10,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  transition: 'transform 0.2s ease',
};

const GraphExplorerPro = ({
  data,
  loading = false,
  onNodeClick,
  onQueryNode,
  graphragService,
  tenantId = 'trustx-demo',
  height = 700,
  noResults = false,
  noResultsSuggestions = [],
  onSuggestionClick,
  expandedNodeIds = new Set(),
  reviewMode = false,
  reviewDecisions = {},
  onReviewDecision,
  selectedReviewUser,
}) => {
  const theme = useTheme();
  const graphRef = useRef();
  const containerRef = useRef();

  // Dimensions -- start at 0; ForceGraph won't render until ResizeObserver provides real size
  const [dimensions, setDimensions] = useState({ width: 0, height });
  const needsAutoFit = useRef(false);
  const prevNodeCount = useRef(0);

  // Panel states
  const [leftPanelOpen, setLeftPanelOpen] = useState(false);
  const [rightPanelOpen, setRightPanelOpen] = useState(false);

  // Edge label toggle -- on by default
  const [showEdgeLabels, setShowEdgeLabels] = useState(true);

  // Selection state
  const [selectedNode, setSelectedNode] = useState(null);
  const [hoveredNode, setHoveredNode] = useState(null);

  // User detail popup state
  const [showUserPopup, setShowUserPopup] = useState(false);
  const [popupUser, setPopupUser] = useState(null);
  const clickTimerRef = useRef(null);
  const keepPopupOnDataChange = useRef(false);

  // Reset selection/popup when data changes (new query results)
  useEffect(() => {
    if (keepPopupOnDataChange.current) {
      keepPopupOnDataChange.current = false;
    } else {
      setShowUserPopup(false);
      setPopupUser(null);
    }
    setSelectedNode(null);
    setLeftPanelOpen(false);
  }, [data]);

  const lastClickNodeRef = useRef(null);

  // Filter states
  const [nodeTypeFilters, setNodeTypeFilters] = useState({});
  const [edgeTypeFilters, setEdgeTypeFilters] = useState({});
  const [propertyFilters, setPropertyFilters] = useState({});
  const [searchQuery, setSearchQuery] = useState('');

  // Computed: Extract unique types and build filter state
  const { nodeTypes, edgeTypes, numericProperties } = useMemo(() => {
    if (!data?.nodes) return { nodeTypes: {}, edgeTypes: {}, numericProperties: {} };

    const nodeTypes = {};
    const edgeTypes = {};
    const numericProps = {};

    data.nodes.forEach(node => {
      const type = node.type || node.entity_type || 'default';
      nodeTypes[type] = (nodeTypes[type] || 0) + 1;

      Object.entries(node.properties || node).forEach(([key, value]) => {
        if (typeof value === 'number' && !['x', 'y', 'vx', 'vy', 'index'].includes(key)) {
          if (!numericProps[key]) {
            numericProps[key] = { min: value, max: value };
          } else {
            numericProps[key].min = Math.min(numericProps[key].min, value);
            numericProps[key].max = Math.max(numericProps[key].max, value);
          }
        }
      });
    });

    (data.links || data.edges || []).forEach(link => {
      const type = link.type || link.relationship || 'RELATED';
      edgeTypes[type] = (edgeTypes[type] || 0) + 1;
    });

    return { nodeTypes, edgeTypes, numericProperties: numericProps };
  }, [data]);

  // Initialize filters when data changes
  useEffect(() => {
    const initialNodeFilters = {};
    Object.keys(nodeTypes).forEach(type => {
      initialNodeFilters[type] = true;
    });
    setNodeTypeFilters(initialNodeFilters);

    const initialEdgeFilters = {};
    Object.keys(edgeTypes).forEach(type => {
      initialEdgeFilters[type] = true;
    });
    setEdgeTypeFilters(initialEdgeFilters);

    const initialPropertyFilters = {};
    Object.entries(numericProperties).forEach(([key, range]) => {
      initialPropertyFilters[key] = { ...range, enabled: false };
    });
    setPropertyFilters(initialPropertyFilters);
  }, [nodeTypes, edgeTypes, numericProperties]);

  // Filtered graph data
  const filteredData = useMemo(() => {
    if (!data?.nodes) return { nodes: [], links: [] };

    const hasNodeFilters = Object.keys(nodeTypeFilters).length > 0;
    const hasEdgeFilters = Object.keys(edgeTypeFilters).length > 0;

    let filteredNodes = data.nodes.filter(node => {
      const type = node.type || node.entity_type || 'default';

      if (hasNodeFilters && !nodeTypeFilters[type]) return false;

      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const searchable = [
          node.name, node.displayName, node.email, node.label, node.id
        ].filter(Boolean).join(' ').toLowerCase();
        if (!searchable.includes(query)) return false;
      }

      for (const [key, filter] of Object.entries(propertyFilters)) {
        if (filter.enabled) {
          const value = node[key] || node.properties?.[key];
          if (typeof value === 'number') {
            if (value < filter.min || value > filter.max) return false;
          }
        }
      }

      return true;
    });

    const nodeIds = new Set(filteredNodes.map(n => n.id || n.email || n.name));

    const filteredLinks = (data.links || data.edges || []).filter(link => {
      const type = link.type || link.relationship || 'RELATED';

      if (hasEdgeFilters && !edgeTypeFilters[type]) return false;

      const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
      const targetId = typeof link.target === 'object' ? link.target.id : link.target;

      return nodeIds.has(sourceId) && nodeIds.has(targetId);
    });

    const processedNodes = filteredNodes.map(node => ({
      ...node,
      id: node.id || node.email || node.name,
      label: node.displayName || node.name || node.email || node.id,
      type: node.type || node.entity_type || 'default',
      color: NODE_COLORS[node.type || node.entity_type] || NODE_COLORS.default,
      size: NODE_SIZES[node.type || node.entity_type] || NODE_SIZES.default,
    }));

    return { nodes: processedNodes, links: filteredLinks };
  }, [data, nodeTypeFilters, edgeTypeFilters, propertyFilters, searchQuery]);

  // Update dimensions on resize.
  useEffect(() => {
    const updateDimensions = () => {
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;
      const leftWidth = leftPanelOpen ? 260 : 0;
      const filterWidth = rightPanelOpen ? 220 : 0;
      const graphWidth = Math.max(Math.floor(rect.width) - leftWidth - filterWidth, 400);
      const graphHeight = Math.max(Math.floor(rect.height), 400);
      setDimensions(prev => {
        if (prev.width === graphWidth && prev.height === graphHeight) return prev;
        return { width: graphWidth, height: graphHeight };
      });
    };

    const t1 = setTimeout(updateDimensions, 50);
    const t2 = setTimeout(updateDimensions, 300);

    let resizeObserver;
    if (containerRef.current && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(() => {
        updateDimensions();
      });
      resizeObserver.observe(containerRef.current);
    }

    window.addEventListener('resize', updateDimensions);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener('resize', updateDimensions);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [leftPanelOpen, rightPanelOpen, data]);

  // Configure d3 forces for readable graph layout
  useEffect(() => {
    if (!graphRef.current || dimensions.width === 0) return;
    const fg = graphRef.current;
    const nodeCount = filteredData.nodes?.length || 0;
    if (nodeCount === 0) return;

    const wasExpand = prevNodeCount.current > 0 && nodeCount > prevNodeCount.current;
    prevNodeCount.current = nodeCount;

    if (fg.d3Force('charge')) fg.d3Force('charge').strength(-120);
    if (fg.d3Force('link')) fg.d3Force('link').distance(50);
    fg.d3ReheatSimulation();

    if (!wasExpand) {
      needsAutoFit.current = true;
    }
  }, [filteredData, dimensions.width]);

  // Auto zoom-to-fit when simulation engine stops
  const handleEngineStop = useCallback(() => {
    if (needsAutoFit.current && graphRef.current) {
      graphRef.current.zoomToFit(400, 30);
      needsAutoFit.current = false;
    }
  }, []);

  // Draw hexagon helper
  const drawHexagon = (ctx, x, y, size) => {
    ctx.beginPath();
    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI / 3) * i - Math.PI / 2;
      const px = x + size * Math.cos(angle);
      const py = y + size * Math.sin(angle);
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.closePath();
  };

  // Draw star helper
  const drawStar = (ctx, x, y, size) => {
    const spikes = 5;
    const outerRadius = size;
    const innerRadius = size / 2;

    ctx.beginPath();
    for (let i = 0; i < spikes * 2; i++) {
      const radius = i % 2 === 0 ? outerRadius : innerRadius;
      const angle = (Math.PI / spikes) * i - Math.PI / 2;
      const px = x + radius * Math.cos(angle);
      const py = y + radius * Math.sin(angle);
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.closePath();
  };

  // Custom node rendering with shapes + glow effects for dark canvas
  const drawNode = useCallback((node, ctx, globalScale) => {
    if (!isFinite(node.x) || !isFinite(node.y)) return;

    const label = node.label || node.id;
    const fontSize = Math.max(11 / globalScale, 4);
    const nodeSize = node.size || 12;
    const shape = NODE_SHAPES[node.type] || NODE_SHAPES.default;
    const icon = NODE_ICONS[node.type] || NODE_ICONS.default;

    const isSelected = node === selectedNode;
    const isHovered = node === hoveredNode;
    const baseColor = node.color || NODE_COLORS.default;
    const nodeColor = isSelected ? '#fbbf24' : isHovered ? '#fcd34d' : baseColor;
    const glowColor = isSelected
      ? 'rgba(251, 191, 36, 0.5)'
      : isHovered
        ? 'rgba(252, 211, 77, 0.4)'
        : (NODE_GLOW_COLORS[node.type] || NODE_GLOW_COLORS.default);

    // --- Outer glow halo ---
    ctx.save();
    ctx.beginPath();
    ctx.arc(node.x, node.y, nodeSize + (isSelected ? 10 : isHovered ? 8 : 5), 0, 2 * Math.PI);
    const gradient = ctx.createRadialGradient(
      node.x, node.y, nodeSize * 0.5,
      node.x, node.y, nodeSize + (isSelected ? 12 : 6)
    );
    gradient.addColorStop(0, glowColor);
    gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.fillStyle = gradient;
    ctx.fill();
    ctx.restore();

    // --- Main node shape ---
    ctx.beginPath();
    switch (shape) {
      case 'hexagon':
        drawHexagon(ctx, node.x, node.y, nodeSize);
        break;
      case 'square':
        ctx.rect(node.x - nodeSize, node.y - nodeSize, nodeSize * 2, nodeSize * 2);
        break;
      case 'diamond':
        ctx.moveTo(node.x, node.y - nodeSize);
        ctx.lineTo(node.x + nodeSize, node.y);
        ctx.lineTo(node.x, node.y + nodeSize);
        ctx.lineTo(node.x - nodeSize, node.y);
        ctx.closePath();
        break;
      case 'pentagon':
        for (let i = 0; i < 5; i++) {
          const angle = (Math.PI * 2 / 5) * i - Math.PI / 2;
          const px = node.x + nodeSize * Math.cos(angle);
          const py = node.y + nodeSize * Math.sin(angle);
          if (i === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        }
        ctx.closePath();
        break;
      case 'triangle':
        ctx.moveTo(node.x, node.y - nodeSize);
        ctx.lineTo(node.x + nodeSize, node.y + nodeSize * 0.7);
        ctx.lineTo(node.x - nodeSize, node.y + nodeSize * 0.7);
        ctx.closePath();
        break;
      case 'star':
        drawStar(ctx, node.x, node.y, nodeSize);
        break;
      default: // circle
        ctx.arc(node.x, node.y, nodeSize, 0, 2 * Math.PI);
    }

    // Gradient fill for depth
    const fillGradient = ctx.createRadialGradient(
      node.x - nodeSize * 0.3, node.y - nodeSize * 0.3, 0,
      node.x, node.y, nodeSize * 1.2
    );
    fillGradient.addColorStop(0, lightenColor(nodeColor, 30));
    fillGradient.addColorStop(1, nodeColor);
    ctx.fillStyle = fillGradient;
    ctx.fill();

    // Luminous border
    ctx.strokeStyle = isSelected ? '#fbbf24' : isHovered ? '#fcd34d' : 'rgba(255,255,255,0.15)';
    ctx.lineWidth = isSelected ? 2.5 : isHovered ? 2 : 1;
    ctx.stroke();

    // --- Review decision ring ---
    if (reviewMode && node.email && reviewDecisions[node.email]) {
      const decision = reviewDecisions[node.email].decision;
      const ringColor = DECISION_COLORS[decision];
      if (ringColor) {
        ctx.beginPath();
        ctx.arc(node.x, node.y, nodeSize + 3, 0, 2 * Math.PI);
        ctx.strokeStyle = ringColor;
        ctx.lineWidth = 2.5;
        ctx.stroke();
      }
    }

    // --- Icon inside node ---
    if (globalScale > 0.6 && nodeSize >= 14) {
      ctx.font = `${Math.max(nodeSize * 0.7, 8)}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = 'rgba(255,255,255,0.9)';
      ctx.fillText(icon, node.x, node.y);
    }

    // --- Label below node ---
    if (globalScale > 0.5) {
      ctx.font = `500 ${fontSize}px Inter, system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
      ctx.fillText(label, node.x + 1, node.y + nodeSize + 5);
      ctx.fillStyle = '#e2e8f0';
      ctx.fillText(label, node.x, node.y + nodeSize + 4);

      // --- Job title below name (User nodes only) ---
      const jobTitle = node.properties?.jobTitle || node.jobTitle;
      if (node.type === 'User' && jobTitle) {
        const titleFontSize = Math.max(9 / globalScale, 3);
        ctx.font = `${titleFontSize}px Inter, system-ui, sans-serif`;
        const titleY = node.y + nodeSize + 4 + fontSize + 3;
        ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
        ctx.fillText(jobTitle, node.x + 1, titleY + 1);
        ctx.fillStyle = '#94a3b8';
        ctx.fillText(jobTitle, node.x, titleY);
      }
    }

    // --- Expand indicator ("+" badge) ---
    const nodeId = node.id || node.email || node.name;
    if (nodeId && !expandedNodeIds.has(nodeId) && globalScale > 0.4) {
      const badgeR = Math.max(5, nodeSize * 0.35);
      const bx = node.x + nodeSize * 0.8;
      const by = node.y - nodeSize * 0.8;

      ctx.beginPath();
      ctx.arc(bx, by, badgeR, 0, 2 * Math.PI);
      ctx.fillStyle = 'rgba(59, 130, 246, 0.85)';
      ctx.fill();
      ctx.strokeStyle = 'rgba(255,255,255,0.6)';
      ctx.lineWidth = 0.8;
      ctx.stroke();

      ctx.font = `bold ${badgeR * 1.4}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = '#fff';
      ctx.fillText('+', bx, by);
    }
  }, [selectedNode, hoveredNode, expandedNodeIds, reviewMode, reviewDecisions]);

  // Helper: lighten a hex color
  const lightenColor = (hex, percent) => {
    const num = parseInt(hex.replace('#', ''), 16);
    const r = Math.min(255, (num >> 16) + percent);
    const g = Math.min(255, ((num >> 8) & 0x00FF) + percent);
    const b = Math.min(255, (num & 0x0000FF) + percent);
    return `rgb(${r},${g},${b})`;
  };

  // Render relationship label at the midpoint of a curved edge
  const drawEdgeLabel = useCallback((link, ctx, globalScale) => {
    const rawType = link.type || link.relationship || '';
    if (!rawType) return;
    const label = rawType.replace(/_/g, ' ');

    const start = link.source;
    const end = link.target;
    if (
      !start || !end ||
      !isFinite(start.x) || !isFinite(start.y) ||
      !isFinite(end.x) || !isFinite(end.y)
    ) return;

    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const midX = (start.x + end.x) / 2 + dy * 0.15 * 0.5;
    const midY = (start.y + end.y) / 2 - dx * 0.15 * 0.5;

    const fontSize = Math.max(8 / globalScale, 3);
    ctx.font = `500 ${fontSize}px Inter, system-ui, sans-serif`;

    const textWidth = ctx.measureText(label).width;
    const pad = 3 / globalScale;
    const bgH = fontSize + pad * 2;

    ctx.fillStyle = 'rgba(10, 15, 30, 0.78)';
    ctx.beginPath();
    const rx = midX - textWidth / 2 - pad;
    const ry = midY - bgH / 2;
    const rw = textWidth + pad * 2;
    const rh = bgH;
    const r = rh / 2;
    ctx.moveTo(rx + r, ry);
    ctx.lineTo(rx + rw - r, ry);
    ctx.arcTo(rx + rw, ry, rx + rw, ry + rh, r);
    ctx.lineTo(rx + rw, ry + rh - r);
    ctx.arcTo(rx + rw, ry + rh, rx + rw - r, ry + rh, r);
    ctx.lineTo(rx + r, ry + rh);
    ctx.arcTo(rx, ry + rh, rx, ry + rh - r, r);
    ctx.lineTo(rx, ry + r);
    ctx.arcTo(rx, ry, rx + r, ry, r);
    ctx.closePath();
    ctx.fill();

    ctx.fillStyle = 'rgba(203, 213, 225, 0.92)';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, midX, midY);
  }, []);

  // Handle node hover
  const handleNodeHover = useCallback((node) => {
    setHoveredNode(node);
    if (containerRef.current) {
      containerRef.current.style.cursor = node ? 'pointer' : 'default';
    }
  }, []);

  // Handle node click
  const handleNodeClick = useCallback((node) => {
    const nodeId = node.id || node.email || node.name;
    const prevNodeId = lastClickNodeRef.current;

    if (clickTimerRef.current && prevNodeId === nodeId) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
      lastClickNodeRef.current = null;
      const nodeType = node.type || node.entity_type;
      if (nodeType === 'User') {
        setSelectedNode(node);
        setPopupUser(node);
        setShowUserPopup(true);
        keepPopupOnDataChange.current = true;
      }
      if (onQueryNode) onQueryNode(node);
      return;
    }

    if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
    lastClickNodeRef.current = nodeId;

    clickTimerRef.current = setTimeout(() => {
      clickTimerRef.current = null;
      lastClickNodeRef.current = null;
      setSelectedNode(node);

      const nodeType = node.type || node.entity_type;
      if (nodeType !== 'User' && showUserPopup) {
        setShowUserPopup(false);
        setPopupUser(null);
      }

      if (onNodeClick) onNodeClick(node);
      if (onQueryNode) onQueryNode(node);
    }, 800);
  }, [onNodeClick, onQueryNode, showUserPopup]);

  // Handle popup close
  const handlePopupClose = useCallback(() => {
    setShowUserPopup(false);
    setPopupUser(null);
  }, []);

  // Handle explore from popup
  const handleExploreUser = useCallback((user) => {
    handlePopupClose();
    if (onQueryNode) onQueryNode(user);
  }, [onQueryNode, handlePopupClose]);

  // Zoom controls
  const handleZoomIn = () => graphRef.current?.zoom(graphRef.current.zoom() * 1.5, 400);
  const handleZoomOut = () => graphRef.current?.zoom(graphRef.current.zoom() / 1.5, 400);
  const handleZoomFit = () => graphRef.current?.zoomToFit(400, 50);
  const handleCenterOnSelected = () => {
    if (graphRef.current && selectedNode) {
      graphRef.current.centerAt(selectedNode.x, selectedNode.y, 400);
      graphRef.current.zoom(2, 400);
    }
  };

  // Toggle filters
  const toggleNodeType = (type) => {
    setNodeTypeFilters(prev => ({ ...prev, [type]: !prev[type] }));
  };

  const toggleEdgeType = (type) => {
    setEdgeTypeFilters(prev => ({ ...prev, [type]: !prev[type] }));
  };

  const toggleAllNodeTypes = (enabled) => {
    const newFilters = {};
    Object.keys(nodeTypeFilters).forEach(type => {
      newFilters[type] = enabled;
    });
    setNodeTypeFilters(newFilters);
  };

  const toggleAllEdgeTypes = (enabled) => {
    const newFilters = {};
    Object.keys(edgeTypeFilters).forEach(type => {
      newFilters[type] = enabled;
    });
    setEdgeTypeFilters(newFilters);
  };

  const togglePropertyFilter = (key) => {
    setPropertyFilters(prev => ({
      ...prev,
      [key]: { ...prev[key], enabled: !prev[key].enabled }
    }));
  };

  const updatePropertyFilter = (key, field, value) => {
    setPropertyFilters(prev => ({
      ...prev,
      [key]: { ...prev[key], [field]: Number(value) }
    }));
  };

  // Get selected node properties for display
  const getNodeProperties = (node) => {
    if (!node) return [];

    const skip = ['x', 'y', 'vx', 'vy', 'index', 'fx', 'fy', '__indexColor', 'color', 'size'];
    const props = [];

    if (node.email) props.push({ key: 'email', value: node.email });
    if (node.name && node.name !== node.email) props.push({ key: 'name', value: node.name });
    if (node.department) props.push({ key: 'department', value: node.department });
    if (node.jobTitle) props.push({ key: 'jobTitle', value: node.jobTitle });
    if (node.status) props.push({ key: 'status', value: node.status });

    if (node.riskScore !== undefined || node.risk_score !== undefined) {
      props.push({ key: 'riskScore', value: node.riskScore || node.risk_score, isRisk: true });
    }
    if (node.riskLevel || node.risk_level) {
      props.push({ key: 'riskLevel', value: node.riskLevel || node.risk_level, isRisk: true });
    }
    if (node.daysInactive !== undefined || node.days_inactive !== undefined) {
      props.push({ key: 'daysInactive', value: node.daysInactive || node.days_inactive });
    }
    if (node.zScore !== undefined || node.z_score !== undefined) {
      props.push({ key: 'zScore', value: Number(node.zScore || node.z_score).toFixed(2) });
    }

    const allProps = { ...node, ...(node.properties || {}) };
    Object.entries(allProps).forEach(([key, value]) => {
      if (!skip.includes(key) && value !== null && value !== undefined &&
        !props.find(p => p.key === key || p.key === key.replace(/_/g, ''))) {
        if (typeof value !== 'object') {
          props.push({ key, value });
        }
      }
    });

    return props;
  };

  // Canvas background color from theme
  const canvasBg = theme.palette.background.default;

  // Empty state
  if (!data?.nodes?.length && !loading) {
    return (
      <Box
        ref={containerRef}
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          bgcolor: canvasBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box sx={{ textAlign: 'center', maxWidth: 400, px: 3 }}>
          {noResults ? (
            <>
              <SearchX size={36} style={{ color: 'rgba(255,255,255,0.3)', marginBottom: 16 }} />
              <Typography variant="h6" sx={{ mb: 1 }}>No results found</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                No information found for the given query. Try a different question or one of the suggestions below.
              </Typography>
              {noResultsSuggestions.length > 0 && (
                <Stack direction="row" flexWrap="wrap" gap={1} justifyContent="center">
                  {noResultsSuggestions.map((s, i) => (
                    <Chip
                      key={i}
                      label={s}
                      variant="outlined"
                      size="small"
                      onClick={() => onSuggestionClick?.(s)}
                      sx={{
                        borderColor: 'divider',
                        '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
                      }}
                    />
                  ))}
                </Stack>
              )}
            </>
          ) : (
            <>
              {/* Animated rings */}
              <Box sx={{ position: 'relative', width: 120, height: 120, mx: 'auto', mb: 3 }}>
                {[1, 2, 3].map((i) => (
                  <Box
                    key={i}
                    sx={{
                      position: 'absolute',
                      inset: `${(i - 1) * 12}px`,
                      borderRadius: '50%',
                      border: 1,
                      borderColor: `rgba(96, 165, 250, ${0.3 - i * 0.08})`,
                      animation: `gepPulse ${2 + i * 0.5}s ease-in-out infinite`,
                      '@keyframes gepPulse': {
                        '0%, 100%': { opacity: 0.3, transform: 'scale(1)' },
                        '50%': { opacity: 0.8, transform: 'scale(1.05)' },
                      },
                    }}
                  />
                ))}
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Network size={32} style={{ color: 'rgba(96,165,250,0.6)' }} />
                </Box>
              </Box>
              <Typography variant="h6" sx={{ mb: 1 }}>Knowledge Graph Explorer</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Ask a question in the chat to visualize your identity graph
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                justifyContent="center"
                alignItems="center"
                sx={{ color: 'text.secondary', fontSize: '0.75rem' }}
              >
                <Typography variant="caption">Users</Typography>
                <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'divider' }} />
                <Typography variant="caption">Groups</Typography>
                <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'divider' }} />
                <Typography variant="caption">Applications</Typography>
                <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'divider' }} />
                <Typography variant="caption">Risks</Typography>
              </Stack>
            </>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        bgcolor: canvasBg,
        overflow: 'hidden',
      }}
    >
      {/* Loading overlay */}
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0,0,0,0.5)',
            zIndex: 20,
          }}
        >
          <CircularProgress size={32} sx={{ mb: 1 }} />
          <Typography variant="body2" color="text.secondary">Loading graph...</Typography>
        </Box>
      )}

      {/* Left Panel - Node Properties */}
      <Box
        sx={{
          ...panelSx,
          left: 0,
          transform: leftPanelOpen ? 'translateX(0)' : 'translateX(-260px)',
        }}
      >
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {selectedNode ? (
            <>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Chip
                  label={selectedNode.type || 'Node'}
                  size="small"
                  sx={{
                    bgcolor: NODE_COLORS[selectedNode.type] || NODE_COLORS.default,
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: '0.7rem',
                  }}
                />
                <IconButton size="small" onClick={() => setSelectedNode(null)}>
                  <X size={14} />
                </IconButton>
              </Stack>

              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>{selectedNode.label}</Typography>

              <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                Properties
              </Typography>
              {getNodeProperties(selectedNode).map(({ key, value, isRisk }) => (
                <Stack
                  key={key}
                  direction="row"
                  justifyContent="space-between"
                  sx={{
                    py: 0.5,
                    borderBottom: 1,
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {formatPropertyKey(key)}
                  </Typography>
                  <Typography
                    variant="caption"
                    fontWeight={500}
                    sx={{ color: isRisk ? getRiskColor(value) : 'text.primary' }}
                  >
                    {formatPropertyValue(value)}
                  </Typography>
                </Stack>
              ))}

              {onQueryNode && (
                <Box
                  component="button"
                  onClick={() => onQueryNode(selectedNode)}
                  sx={{
                    mt: 2,
                    width: '100%',
                    py: 1,
                    px: 2,
                    border: 1,
                    borderColor: 'primary.main',
                    borderRadius: 1,
                    bgcolor: 'transparent',
                    color: 'primary.main',
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  Explore this {selectedNode.type || 'node'}
                </Box>
              )}
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
              <Crosshair size={32} strokeWidth={1} style={{ marginBottom: 8, opacity: 0.5 }} />
              <Typography variant="body2">Select a node to view its properties</Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Panel toggle - Properties */}
      <Tooltip title={leftPanelOpen ? 'Hide properties' : 'Show properties'} placement="right">
        <IconButton
          size="small"
          onClick={() => setLeftPanelOpen(!leftPanelOpen)}
          sx={{
            position: 'absolute',
            left: leftPanelOpen ? 260 : 0,
            top: 8,
            zIndex: 11,
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'divider',
            borderRadius: '0 4px 4px 0',
            borderLeft: 'none',
            width: 24,
            height: 32,
            transition: 'left 0.2s ease',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          {leftPanelOpen ? <ChevronLeft size={14} /> : <ChevronRight size={14} />}
        </IconButton>
      </Tooltip>

      {/* Filter Panel */}
      <Box
        sx={{
          ...panelSx,
          left: leftPanelOpen ? 260 : 0,
          transform: rightPanelOpen
            ? 'translateX(0)'
            : `translateX(-260px)`,
          transition: 'transform 0.2s ease, left 0.2s ease',
        }}
      >
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <Filter size={16} />
            <Typography variant="subtitle2">Filters</Typography>
          </Stack>

          {/* Search */}
          <TextField
            fullWidth
            size="small"
            placeholder="Search nodes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={14} style={{ color: 'rgba(255,255,255,0.4)' }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery('')}>
                    <X size={12} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { fontSize: '0.8rem' } }}
          />

          {/* Node Types */}
          <Box sx={{ mb: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="caption" fontWeight={600}>Nodes</Typography>
              <Stack direction="row" spacing={0.5}>
                <Typography
                  variant="caption"
                  onClick={() => toggleAllNodeTypes(true)}
                  sx={{ cursor: 'pointer', color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}
                >
                  All
                </Typography>
                <Typography
                  variant="caption"
                  onClick={() => toggleAllNodeTypes(false)}
                  sx={{ cursor: 'pointer', color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}
                >
                  None
                </Typography>
              </Stack>
            </Stack>

            {Object.entries(nodeTypes).map(([type, count]) => (
              <Box
                key={type}
                onClick={() => toggleNodeType(type)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  py: 0.5,
                  px: 1,
                  borderRadius: 0.5,
                  cursor: 'pointer',
                  opacity: nodeTypeFilters[type] ? 1 : 0.4,
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    bgcolor: NODE_COLORS[type] || NODE_COLORS.default,
                    flexShrink: 0,
                  }}
                />
                <Typography variant="caption" sx={{ flex: 1 }}>{type}</Typography>
                <Typography variant="caption" color="text.secondary">{count}</Typography>
                {nodeTypeFilters[type]
                  ? <Eye size={12} style={{ color: 'rgba(255,255,255,0.4)' }} />
                  : <EyeOff size={12} style={{ color: 'rgba(255,255,255,0.2)' }} />
                }
              </Box>
            ))}
          </Box>

          {/* Edge Types */}
          <Box sx={{ mb: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="caption" fontWeight={600}>Edges</Typography>
              <Stack direction="row" spacing={0.5}>
                <Typography
                  variant="caption"
                  onClick={() => toggleAllEdgeTypes(true)}
                  sx={{ cursor: 'pointer', color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}
                >
                  All
                </Typography>
                <Typography
                  variant="caption"
                  onClick={() => toggleAllEdgeTypes(false)}
                  sx={{ cursor: 'pointer', color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}
                >
                  None
                </Typography>
              </Stack>
            </Stack>

            {Object.entries(edgeTypes).map(([type, count]) => (
              <Box
                key={type}
                onClick={() => toggleEdgeType(type)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  py: 0.5,
                  px: 1,
                  borderRadius: 0.5,
                  cursor: 'pointer',
                  opacity: edgeTypeFilters[type] ? 1 : 0.4,
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <ArrowRight size={12} style={{ color: 'rgba(255,255,255,0.4)', flexShrink: 0 }} />
                <Typography variant="caption" sx={{ flex: 1 }}>
                  {type.replace(/_/g, ' ')}
                </Typography>
                <Typography variant="caption" color="text.secondary">{count}</Typography>
                {edgeTypeFilters[type]
                  ? <Eye size={12} style={{ color: 'rgba(255,255,255,0.4)' }} />
                  : <EyeOff size={12} style={{ color: 'rgba(255,255,255,0.2)' }} />
                }
              </Box>
            ))}
          </Box>

          {/* Property Filters */}
          {Object.keys(numericProperties).length > 0 && (
            <Box>
              <Typography variant="caption" fontWeight={600} sx={{ mb: 1, display: 'block' }}>
                Properties
              </Typography>
              {Object.entries(numericProperties).map(([key, range]) => {
                const filter = propertyFilters[key];
                if (!filter) return null;

                return (
                  <Box key={key} sx={{ mb: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={filter.enabled}
                          onChange={() => togglePropertyFilter(key)}
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="caption">{formatPropertyKey(key)}</Typography>
                      }
                    />
                    {filter.enabled && (
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ pl: 3.5 }}>
                        <TextField
                          type="number"
                          size="small"
                          value={filter.min}
                          inputProps={{ min: range.min, max: range.max }}
                          onChange={(e) => updatePropertyFilter(key, 'min', e.target.value)}
                          sx={{ width: 70, '& input': { fontSize: '0.75rem', py: 0.5 } }}
                        />
                        <Typography variant="caption">to</Typography>
                        <TextField
                          type="number"
                          size="small"
                          value={filter.max}
                          inputProps={{ min: range.min, max: range.max }}
                          onChange={(e) => updatePropertyFilter(key, 'max', e.target.value)}
                          sx={{ width: 70, '& input': { fontSize: '0.75rem', py: 0.5 } }}
                        />
                      </Stack>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>

      {/* Panel toggle - Filters */}
      <Tooltip title={rightPanelOpen ? 'Hide filters' : 'Show filters'} placement="right">
        <IconButton
          size="small"
          onClick={() => setRightPanelOpen(!rightPanelOpen)}
          sx={{
            position: 'absolute',
            left: rightPanelOpen
              ? (leftPanelOpen ? 520 : 260)
              : (leftPanelOpen ? 260 : 0),
            top: 44,
            zIndex: 11,
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'divider',
            borderRadius: '0 4px 4px 0',
            borderLeft: 'none',
            width: 24,
            height: 32,
            transition: 'left 0.2s ease',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          {rightPanelOpen ? <ChevronLeft size={14} /> : <Filter size={14} />}
        </IconButton>
      </Tooltip>

      {/* Center - Graph Visualization */}
      <Box sx={{ position: 'absolute', inset: 0 }}>
        {/* Zoom controls */}
        <Stack
          spacing={0.5}
          sx={{
            position: 'absolute',
            right: 12,
            top: 12,
            zIndex: 10,
          }}
        >
          {[
            { icon: <ZoomIn size={16} />, onClick: handleZoomIn, title: 'Zoom In' },
            { icon: <ZoomOut size={16} />, onClick: handleZoomOut, title: 'Zoom Out' },
            { icon: <Maximize size={16} />, onClick: handleZoomFit, title: 'Fit to View' },
          ].map((btn) => (
            <Tooltip key={btn.title} title={btn.title} placement="left">
              <IconButton
                size="small"
                onClick={btn.onClick}
                sx={{
                  bgcolor: 'background.paper',
                  border: 1,
                  borderColor: 'divider',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                {btn.icon}
              </IconButton>
            </Tooltip>
          ))}
          {selectedNode && (
            <Tooltip title="Center on Selected" placement="left">
              <IconButton
                size="small"
                onClick={handleCenterOnSelected}
                sx={{
                  bgcolor: 'background.paper',
                  border: 1,
                  borderColor: 'divider',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Target size={16} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={showEdgeLabels ? 'Hide edge labels' : 'Show edge labels'} placement="left">
            <IconButton
              size="small"
              onClick={() => setShowEdgeLabels(v => !v)}
              sx={{
                bgcolor: 'background.paper',
                border: 1,
                borderColor: showEdgeLabels ? 'rgba(96,165,250,0.4)' : 'divider',
                color: showEdgeLabels ? '#60a5fa' : 'text.secondary',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <ArrowRight size={16} />
            </IconButton>
          </Tooltip>
        </Stack>

        {/* Stats bar */}
        <Stack
          direction="row"
          spacing={2}
          sx={{
            position: 'absolute',
            left: 12,
            bottom: 12,
            zIndex: 10,
            bgcolor: 'rgba(0,0,0,0.6)',
            borderRadius: 1,
            px: 1.5,
            py: 0.5,
          }}
        >
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Circle size={8} fill="currentColor" style={{ color: 'rgba(255,255,255,0.5)' }} />
            <Typography variant="caption" color="text.secondary">
              {filteredData.nodes.length} nodes
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <ArrowRight size={8} style={{ color: 'rgba(255,255,255,0.5)' }} />
            <Typography variant="caption" color="text.secondary">
              {filteredData.links.length} edges
            </Typography>
          </Stack>
        </Stack>

        {/* Inline Legend */}
        {filteredData.nodes.length > 0 && (
          <Stack
            direction="row"
            spacing={0.5}
            sx={{
              position: 'absolute',
              bottom: 12,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 10,
              bgcolor: 'rgba(0,0,0,0.6)',
              borderRadius: 1,
              px: 1,
              py: 0.5,
            }}
          >
            {Object.entries(nodeTypes).map(([type, count]) => (
              <Chip
                key={type}
                size="small"
                label={
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: NODE_COLORS[type] || NODE_COLORS.default,
                      }}
                    />
                    <span>{type}</span>
                    <span style={{ opacity: 0.6 }}>{count}</span>
                  </Stack>
                }
                onClick={() => toggleNodeType(type)}
                sx={{
                  height: 22,
                  fontSize: '0.65rem',
                  bgcolor: 'transparent',
                  color: 'text.secondary',
                  opacity: nodeTypeFilters[type] ? 1 : 0.4,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              />
            ))}
          </Stack>
        )}

        {/* Force Graph */}
        {dimensions.width > 0 && (
          <ForceGraph2D
            ref={graphRef}
            graphData={filteredData}
            width={dimensions.width}
            height={dimensions.height}
            nodeCanvasObject={drawNode}
            nodePointerAreaPaint={(node, color, ctx) => {
              const size = (node.size || 12) + 6;
              ctx.fillStyle = color;
              ctx.beginPath();
              ctx.arc(node.x, node.y, size, 0, 2 * Math.PI);
              ctx.fill();
            }}
            onNodeHover={handleNodeHover}
            onNodeClick={handleNodeClick}
            onEngineStop={handleEngineStop}
            linkColor={(link) => {
              const type = link.type || link.relationship || 'default';
              return EDGE_COLORS[type] || EDGE_COLORS.default;
            }}
            linkOpacity={0.45}
            linkWidth={(link) => {
              const type = link.type || link.relationship || '';
              if (type.includes('INSIGHT') || type.includes('AFFECT')) return 2.5;
              return 1.5;
            }}
            linkDirectionalArrowLength={6}
            linkDirectionalArrowRelPos={0.95}
            linkDirectionalArrowColor={(link) => {
              const type = link.type || link.relationship || 'default';
              return EDGE_COLORS[type] || EDGE_COLORS.default;
            }}
            linkDirectionalParticles={3}
            linkDirectionalParticleWidth={3}
            linkDirectionalParticleSpeed={0.004}
            linkDirectionalParticleColor={(link) => {
              const type = link.type || link.relationship || 'default';
              return EDGE_COLORS[type] || EDGE_COLORS.default;
            }}
            linkCurvature={0.15}
            linkLabel={(link) => link.type || link.relationship}
            linkCanvasObjectMode={() => showEdgeLabels ? 'after' : undefined}
            linkCanvasObject={showEdgeLabels ? drawEdgeLabel : undefined}
            d3AlphaDecay={0.02}
            d3VelocityDecay={0.25}
            cooldownTime={3000}
            enableNodeDrag={true}
            enableZoomInteraction={true}
            enablePanInteraction={true}
            backgroundColor="#0a0f1e"
          />
        )}
      </Box>

      {/* User Detail Popup */}
      {showUserPopup && popupUser && (
        <UserDetailPopup
          user={popupUser}
          onClose={handlePopupClose}
          onExplore={handleExploreUser}
          graphragService={graphragService}
          tenantId={tenantId}
          reviewMode={reviewMode}
          reviewDecision={popupUser?.email ? reviewDecisions[popupUser.email] : null}
          onReviewDecision={onReviewDecision}
        />
      )}
    </Box>
  );
};

export default GraphExplorerPro;
